import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Package, Globe2, Search, RefreshCw, Download, Upload, AlertTriangle, Percent,
  Plus, Pencil, EyeOff, Eye,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { crmApi, type CrmProduct, type CrmRegion, type CrmSettings } from '../services/api';
import { useAuthStore } from '../store/useAuthStore';
import ProductEditor from '../components/crm/ProductEditor';

type Tab = 'products' | 'regions' | 'discounts';

/** Minor units to a display string. The only place money becomes text. */
function money(minor: number, currency = 'USD'): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency', currency, maximumFractionDigits: 0,
    }).format((minor || 0) / 100);
  } catch {
    return `${((minor || 0) / 100).toFixed(2)}`;
  }
}

export default function CatalogPage() {
  const [tab, setTab] = useState<Tab>('products');
  const [products, setProducts] = useState<CrmProduct[]>([]);
  const [regions, setRegions] = useState<CrmRegion[]>([]);
  const [settings, setSettings] = useState<CrmSettings | null>(null);
  const [catalogs, setCatalogs] = useState<string[]>([]);
  const [catalog, setCatalog] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [editing, setEditing] = useState<CrmProduct | null>(null);
  const [creating, setCreating] = useState(false);

  // Mirrors require_capability on the server. Offering an action the API would
  // refuse is worse than not offering it: the user does the work, then loses it.
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canManage = hasPermission('catalog:manage');
  const canPrice = hasPermission('catalog:admin');

  const setAvailability = async (p: CrmProduct, active: boolean) => {
    if (!active && !confirm(
      `Withdraw ${p.sku} from sale?

` +
      'It stops pricing and cannot be added to a quote. Quotes that already ' +
      'use it keep rendering — this is not a delete.')) return;
    try {
      if (active) await crmApi.activateProduct(p._id);
      else await crmApi.deactivateProduct(p._id);
      toast.success(`${p.sku} ${active ? 'back on sale' : 'withdrawn'}`);
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not change availability');
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, reg, set] = await Promise.all([
        crmApi.listCatalog(),
        crmApi.listRegions(),
        crmApi.getSettings(),
      ]);
      setProducts(cat.products || []);
      setCatalogs(cat.catalogs || []);
      setRegions(reg.regions || []);
      setSettings(set);
    } catch (err: any) {
      toast.error(err.message || 'Could not load the catalog');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /**
   * Download the catalogue. This IS the backup: CouchDB is the only store, so
   * moving a catalogue between environments means exporting from one and
   * restoring into the other. There is no seed file in the image.
   */
  const runExport = async () => {
    setImporting(true);
    try {
      const payload = await crmApi.exportCatalog();
      const stamp = new Date().toISOString().slice(0, 10);
      const url = URL.createObjectURL(
        new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `crm-catalogue-${stamp}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Catalogue exported');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setImporting(false);
    }
  };

  /** Restore an export. Additive by default — it never reverts a live price. */
  const runRestore = async (file: File) => {
    setImporting(true);
    try {
      const payload = JSON.parse(await file.text());
      const preview = await crmApi.restoreCatalog(payload, false);
      if (!confirm(
        `Restore from ${file.name}?\n\n` +
        `${preview.wouldCreate} new, ${preview.wouldSkip} left alone.\n\n` +
        `Products already here keep their current prices — a restore never ` +
        `reverts an edit made in the app.`
      )) return;
      const result = await crmApi.restoreCatalog(payload, true);
      toast.success(
        `Restored — ${result.created} created, ${result.skipped} left alone`);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Restore failed');
    } finally {
      setImporting(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      if (catalog && p.catalogId !== catalog) return false;
      if (!q) return true;
      return [p.sku, p.name, p.familyName].some((v) => (v || '').toLowerCase().includes(q));
    });
  }, [products, search, catalog]);

  const byFamily = useMemo(() => {
    const map = new Map<string, CrmProduct[]>();
    filtered.forEach((p) => {
      const key = `${p.catalogId}/${p.familyName || p.familyId}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(p);
    });
    return [...map.entries()];
  }, [filtered]);

  // The finding worth surfacing: the five stacking rules can sum past the cap.
  const scheduleTotal = useMemo(() => Object.values(settings?.discountSchedule || {})
    .reduce((n, r: any) => n + (r?.discount_pct || 0), 0), [settings]);
  const cap = settings?.discountGovernance?.max_total_discount_pct ?? 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
      </div>
    );
  }

  const tabCls = (t: Tab) =>
    `px-3 py-1.5 text-sm rounded-lg ${tab === t
      ? 'bg-accent/10 text-accent font-medium'
      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`;

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Package size={20} className="text-accent" />
            Catalog &amp; pricing
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Products, regional pricing and the discount rules that govern quotes.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canPrice && (
            <button
              onClick={() => setCreating(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-accent text-white hover:opacity-90"
            >
              <Plus size={14} /> New SKU
            </button>
          )}
          <button
            onClick={runExport}
            disabled={importing}
            title="Download the catalogue — this is the backup"
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-accent hover:text-accent disabled:opacity-50"
          >
            <Download size={14} /> Export
          </button>
          {canPrice && (
            <label
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-accent hover:text-accent cursor-pointer"
              title="Restore an exported catalogue. Never reverts a live price."
            >
              <Upload size={14} /> {importing ? 'Working…' : 'Restore'}
              <input
                type="file"
                accept="application/json"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) void runRestore(f);
                }}
              />
            </label>
          )}
          <button
            onClick={load}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-accent hover:text-accent"
          >
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1 mb-5">
        <button className={tabCls('products')} onClick={() => setTab('products')}>
          Products <span className="text-gray-400">{products.length}</span>
        </button>
        <button className={tabCls('regions')} onClick={() => setTab('regions')}>
          Regions <span className="text-gray-400">{regions.length}</span>
        </button>
        <button className={tabCls('discounts')} onClick={() => setTab('discounts')}>
          Discount rules
        </button>
      </div>

      {products.length === 0 && tab !== 'discounts' && (
        <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 mb-5 text-sm text-amber-800 dark:text-amber-300">
          The catalogue is empty. Add SKUs with <strong>New SKU</strong>, or
          <strong> Restore</strong> an export taken from another environment —
          CouchDB is the only store, so there is no seed file to load.
        </div>
      )}

      {tab === 'products' && (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="SKU, name, family…"
                className="pl-8 pr-3 py-1.5 w-64 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
            <select
              value={catalog}
              onChange={(e) => setCatalog(e.target.value)}
              className="px-2 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">All catalogs</option>
              {catalogs.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <span className="text-xs text-gray-400 ml-auto">
              {filtered.length} of {products.length}
            </span>
          </div>

          <div className="space-y-5">
            {byFamily.map(([family, rows]) => (
              <section key={family}>
                <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                  {family.split('/')[1]}
                  <span className="ml-2 font-normal text-gray-400">{rows.length}</span>
                </h2>
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800/60 text-xs text-gray-500 dark:text-gray-400">
                      <tr>
                        <th className="text-left font-medium px-3 py-2">SKU</th>
                        <th className="text-left font-medium px-3 py-2">Product</th>
                        <th className="text-right font-medium px-3 py-2">List</th>
                        <th className="text-right font-medium px-3 py-2">Cost</th>
                        <th className="text-left font-medium px-3 py-2">Billing</th>
                        {canManage && <th className="w-20 px-3 py-2" />}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {rows.map((p) => (
                        <tr key={p._id} className={p.active ? '' : 'opacity-50'}>
                          <td className="px-3 py-2 font-mono text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {p.sku}
                          </td>
                          <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                            {p.name}
                            {!p.active && <span className="ml-2 text-xs text-gray-400">inactive</span>}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-900 dark:text-gray-100">
                            {money(p.basePriceMinor)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-500 dark:text-gray-400">
                            {money(p.costMinor)}
                          </td>
                          <td className="px-3 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {p.billingPeriod}{p.unit ? ` · ${p.unit}` : ''}
                          </td>
                          {canManage && (
                            <td className="px-3 py-2">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => setEditing(p)}
                                  title="Edit"
                                  className="p-1 rounded text-gray-400 hover:text-accent hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                  <Pencil size={14} />
                                </button>
                                <button
                                  onClick={() => setAvailability(p, !p.active)}
                                  title={p.active ? 'Withdraw from sale' : 'Put back on sale'}
                                  className="p-1 rounded text-gray-400 hover:text-accent hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                  {p.active ? <EyeOff size={14} /> : <Eye size={14} />}
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ))}
          </div>
        </>
      )}

      {(creating || editing) && (
        <ProductEditor
          product={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={load}
        />
      )}

      {tab === 'regions' && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/60 text-xs text-gray-500 dark:text-gray-400">
              <tr>
                <th className="text-left font-medium px-3 py-2">Code</th>
                <th className="text-left font-medium px-3 py-2">Region</th>
                <th className="text-left font-medium px-3 py-2">Currency</th>
                <th className="text-right font-medium px-3 py-2">COL index</th>
                <th className="text-right font-medium px-3 py-2">Price offset</th>
                <th className="text-right font-medium px-3 py-2">FX rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {regions.map((r) => (
                <tr key={r._id}>
                  <td className="px-3 py-2 font-mono text-xs text-gray-500 dark:text-gray-400">{r.code}</td>
                  <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{r.name}</td>
                  <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                    {r.currencySymbol} {r.currency}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-500 dark:text-gray-400">{r.colIndex}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-900 dark:text-gray-100">
                    {r.priceOffsetPct}%
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-500 dark:text-gray-400">
                    {r.exchangeRate}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700">
            Rates are approximate and captured onto a quote when it is created,
            never re-read afterwards — so an issued quote never re-prices itself.
          </p>
        </div>
      )}

      {tab === 'discounts' && settings && (
        <div className="space-y-5">
          <section>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
              Hard limits
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                ['Max per line', `${settings.discountGovernance?.max_line_item_discount_pct ?? 0}%`],
                ['Max total', `${cap}%`],
                ['Approval above', `${settings.discountGovernance?.approval_required_above_pct ?? 0}%`],
                ['Approval above', money((settings.discountGovernance?.approval_required_above_usd ?? 0) * 100)],
              ].map(([label, value], i) => (
                <div key={i} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{value}</div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
              Stacking schedule
            </h2>
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {Object.entries(settings.discountSchedule || {}).map(([key, rule]: [string, any]) => (
                    <tr key={key}>
                      <td className="px-3 py-2 text-gray-900 dark:text-gray-100 capitalize">
                        {key.replace(/_/g, ' ')}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium text-gray-900 dark:text-gray-100 w-20">
                        <span className="inline-flex items-center gap-1">
                          <Percent size={11} className="text-gray-400" />{rule.discount_pct}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{rule.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {scheduleTotal > cap && (
            <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-800 dark:text-amber-300 flex gap-3">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <div>
                <strong>These rules stack past the cap.</strong> All five together
                come to {scheduleTotal}%, against a {cap}% total limit. Quoting
                (FE-7) applies the cap <em>after</em> stacking, so a deal earning
                every rule is held at {cap}% — a commercial decision worth
                confirming before the first quote goes out.
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'regions' && regions.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-10 text-center flex flex-col items-center gap-2">
          <Globe2 size={28} className="text-gray-300 dark:text-gray-600" />
          No regions imported yet.
        </p>
      )}
    </div>
  );
}
